<template>
  <div>
    <p
      class="custom-mb-3 small-note-point"
    >
      {{ $t("categorise_unassined_option") }}: {{ unansweredOptionQuantity }}
    </p>
    <div
      class="match-pair-content"
      :class="{'match-pair-content--disabled': isDisabled}"
    >
      <div
        class="match-pair-item"
        :class="{
          'glass--black': isTextColourLight,
          'glass--white': !isTextColourLight,
        }"
        v-for="(option, index) in options"
        :key="index"
        @click="selectedOption = option"
        v-show="option.value"
      >
        <div
          class="audience-flex match-pair-left layout horizontal center"
        >
          <span v-if="option.value" class="pair-left-text">{{ option.value }}</span>
        </div>
        <div
          class="match-pair-right"
          :class="`${getResult(option)}`"
        >
          <div
            class="match-pair-select"
          >
            <div class="default pair-title" v-if="!option.answer">
              {{ $t('Select') }}
            </div>
            <div v-else class="pair-title vertical layout">
              <span class="pair-right-text">
                <AhaKeyV2
                  v-if="option.answerIndex !== null && option.answerIndex !== undefined"
                  :index="option.answerIndex"
                  :backgroundColorMap="CATEGORY_COLOR_MAP"
                />
                <span v-if="option.answer">{{ option.answer }}</span>
              </span>
            </div>

            <i v-if="!result.id && !option.answer" class="arrow-icon fas fa-chevron-down" />

            <AhaSvg
              v-if="result.id"
              name="correct"
              view-box="0 0 18 18"
              maintain-color
              class="aha-option__icon icon-correct"
            />
            <AhaSvg
              v-if="result.id"
              name="incorrect"
              view-box="0 0 18 18"
              maintain-color
              class="aha-option__icon icon-incorrect"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="match-pair-select-box"
      v-if="selectedOption"
    >
      <div class="overlay-box" @click="selectedOption = null" />
      <CategoriseSelectAnswer
        :selectedOption="selectedOption"
        :categories="categories"
        :isFormSubmitted="isFormSubmitted"
        @selectOption="updateAnswer"
        @close="selectedOption = null"
      />
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import CategoriseSelectAnswer from '@/components/quiz/CategoriseSelectAnswer.vue'
import { CATEGORY_COLOR_MAP } from '@/constants/categorise.constants'
import QuizStatuses from '@/constants/QuizStatuses'
import shuffleArrayWithSeed from '@/components/utils/shuffleArrayWithSeed'
import AhaKeyV2 from '../common/KeyV2.vue'

export default {
  components: {
    CategoriseSelectAnswer,
    AhaKeyV2,
  },
  props: {
    result: {
      type: Object,
      default: () => {},
    },
    isFormSubmitted: {
      type: Boolean,
      default: false,
    },
    isWaitingQuizResult: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedOption: null,
      categoriseAnswers: null,
      options: [],
      CATEGORY_COLOR_MAP,
    }
  },
  computed: {
    ...mapState({
      audienceId: (state) => state.audienceId,
      categories: (_, getters) => {
        const categories = JSON.parse(getters.activeActivity.matchingQuestionOptions)
        return categories.map((category) => ({
          id: category.id,
          value: category.value,
          options: category.options.map((option) => option.value),
        }))
      },
      presentation: (state) => state.presentation,
    }),
    ...mapGetters({
      slide: 'activeActivity',
    }),
    ...mapGetters('themeModule', ['isTextColourLight']),
    isDisabled() {
      return this.result.timeAnswer >= this.slide.timeToAnswer ||
        this.isFormSubmitted ||
        this.isWaitingQuizResult
    },
    unansweredOptionQuantity() {
      return this.options.filter((option) => !option.answer).length
    },
  },
  watch: {
    'result.id': function resultUpdated() {
      this.updateResult()
    },
  },
  mounted() {
    this.initOptions()
    this.updateResult()
  },
  methods: {
    initOptions() {
      const options = this.categories.reduce((acc, category) => {
        const categoryOptions = category.options.map((option) => ({
          value: option,
          categoriseId: category.id,
          answer: '',
        }))
        return [...acc, ...categoryOptions]
      }, [])

      const randomSeed = this.presentation.areSlideOptionsShuffling ? this.audienceId : this.slide.id
      this.options = shuffleArrayWithSeed(options, randomSeed)
    },
    updateResult() {
      // update the data base on the result
      if (this.slide.quizStatus === QuizStatuses.RESULT_SCREEN && this.result.id) {
        const { matchingQuestionAnswer } = this.result
        if (!matchingQuestionAnswer || !matchingQuestionAnswer.length) return
        this.options.forEach((option) => {
          const selectedCategoryIndex = matchingQuestionAnswer.findIndex((category) => category.options.includes(option.value))
          option.answer = matchingQuestionAnswer[selectedCategoryIndex].value
          option.answerIndex = selectedCategoryIndex
        })
      }
    },
    getResult(option) {
      if (this.slide.quizStatus !== QuizStatuses.RESULT_SCREEN || !this.result.id) return ''
      if (this.presentation.manualRevealCorrectAnswers && !this.presentation.audiencePacing && !this.slide.showCorrectOption) return ''
      const category = this.categories.find((c) => c.value === option.answer)
      if (!category || !category.options) return 'incorrect'

      return category.options.includes(option.value) ? 'correct' : 'incorrect'
    },
    updateAnswer(category, index) {
      this.selectedOption.answer = category.value
      this.selectedOption.answerIndex = index
      this.slide.categoriseAnswer = this.options
      this.$store.dispatch('updateSlide', this.slide)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";
.match-pair-content {
  line-height: 1.4;
  text-align: left;
}

.glass--white {
  background: rgba(239, 241, 247, 0.8);
}

.glass--black {
  background: rgba(44, 44, 44, 0.8);
}

.match-pair-item {
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: 10px 0;
  border: 1px solid #FFFFFF;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
}

.match-pair-select {
  background-color: #fff;
  padding: 8px;
  border-radius: 4px;
  width: 100%;
  height: 40px;
  position: relative;
  line-height: 19px;
  font-size: 14px;
  border: 2px solid #fff;
  text-overflow: ellipsis;
  color: #333333;
  &.has-img {
    height: auto;

    .letter-index {
      display: none;
    }
  }

  i {
    position: absolute;
    top: 10px;
    right: 10px;
  }
}

.match-pairs-index {
  width: 24px;
  font-weight: bold;
  font-size: 14px;
  color: #fff;
}
.match-pair-left {
  padding: 10px  0 10px 10px;
  font-size: 14px;
  text-align: center;
  word-break: break-word;
  overflow: hidden;

}

.match-pair-select-box {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #00000050;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  z-index: 999;
  padding: 0 10px;
  padding-top: 140px;

  @media (max-width: 400px) {
    padding-left: 0;
    padding-right: 0;
  }
}

.overlay-box {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.aha-option__icon {
  display: none;
  background-color: #fff;
}

.pair-left-img {
  max-width: 100%;
  height: 140px;
  object-fit: contain;
  margin-top: 8px;
  border-radius: 4px;
  margin: 0 auto;
  margin-bottom: 8px;
}

.pair-right-img {
  height: 140px;
  object-fit: contain;
  max-width: 100%;
  border-radius: 4px;
  margin: 0 auto;
  margin-bottom: 8px;
}

.quiz-pair-img {
  background-position: 50% 50%;
  background-size: cover;
  background-repeat: no-repeat;
  width: 140px;
  height: 140px;
  border-radius: 4px;

  &.left-img {
    margin: 8px auto;
  }

  &.right-img {
    margin: 0px auto 8px;
  }
}

.pair-left-text, .pair-right-text {
  display: flex;
  gap: 0.5rem;
  padding: 0 10px;
}

.match-pair-right {
  padding: 8px;
  min-width: 140px;
  width: 100%;

  &.correct {
    .aha-option__icon {
        position: absolute;
        right: 8px;
        top: 8px;
        border: 1px solid #fff;
        border-radius: 10px;

      &.icon-correct {
        display: block;
      }
    }
    .arrow-icon {
      display: none;
    }
    .match-pair-select {
      border: 2px solid #33C173;
      background-color: rgba(51, 193, 115, 0.4);
      color: unset;
    }
  }

  &.incorrect {
    .aha-option__icon {
        position: absolute;
        right: 8px;
        top: 8px;
        border: 1px solid #fff;
        border-radius: 10px;

      &.icon-incorrect {
        display: block;
      }
    }
    .arrow-icon {
      display: none;
    }
    .match-pair-select {
      border: 2px solid #FF4954;
      background-color: rgba(255, 73, 84, 0.5);
      color: unset
    }
  }
}

.pair-title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
  text-align: center;
}

.match-pair-content--disabled {
  .match-pair-item {
    // cursor: not-allowed;
  }
  .match-pair-select {
    background: #e8e8e8;
  }
}

@media screen and (max-width: 420px) {

.pair-title {
  text-align: left;
}
.match-pair-left {
  text-align: left;

}
.pair-left-text, .pair-right-text {
  padding: 0;
}
}
</style>
